import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as FingerprintJS from '@fingerprintjs/fingerprintjs';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  fetchCommerceMobileSignatureStatus,
  fetchIdentityVerification,
  getStoredCommerceSignatureScope,
  signCommerceMobileContract
} from '../services/commerceMobileSignature';
import { usePageTitle } from '../hooks/usePageTitle';
import { formatLongDate } from '../utils/dateFormatter';
import { tnaDecimalToCalculatorPercent } from '../utils/loanCalculator';
import ContractPdfViewerModal from '../components/RequestFlow/Steps/ContractSignature/ContractPdfViewerModal';

const STATUS_MESSAGES = {
  expired: 'El link venció. Pedile al comercio que genere uno nuevo.',
  superseded: 'Este contrato ya no está vigente. Pedile al comercio que genere un nuevo link.',
  not_signable: 'Este contrato no está disponible para firma.'
};

const STEP_SUMMARY = 'summary';
const STEP_SIGN = 'sign';
const STEP_SUCCESS = 'success';

const ORIGINAL_TOKEN_KEY = 'temp_originalToken';
const IDENTITY_VALIDATED_KEY_PREFIX = 'comers_identity_validated_';

const getIdentityValidatedStorageKey = (token) => `${IDENTITY_VALIDATED_KEY_PREFIX}${token}`;

const readIdentityValidated = (token) => {
  if (!token) {
    return false;
  }

  try {
    return sessionStorage.getItem(getIdentityValidatedStorageKey(token)) === '1';
  } catch (error) {
    return false;
  }
};

const writeIdentityValidated = (token) => {
  if (!token) {
    return;
  }

  try {
    sessionStorage.setItem(getIdentityValidatedStorageKey(token), '1');
  } catch (error) {
    // Ignore storage failures; from_step still drives the return path.
  }
};

const getScreenResolution = () => {
  const width = window.screen?.width || window.innerWidth || 0;
  const height = window.screen?.height || window.innerHeight || 0;
  return `${width}x${height}`;
};

const loadFingerprintAgent = async () => {
  const loader = FingerprintJS.load || FingerprintJS.default?.load;
  if (!loader) {
    return {
      get: async () => ({ visitorId: 'fingerprint-unavailable' })
    };
  }

  try {
    return await loader();
  } catch (error) {
    return {
      get: async () => ({ visitorId: 'fingerprint-unavailable' })
    };
  }
};

const getFingerprintVisitorId = async () => {
  const fingerprintAgent = await loadFingerprintAgent();
  if (typeof fingerprintAgent?.get !== 'function') {
    return 'fingerprint-unavailable';
  }

  try {
    const fingerprintResult = await fingerprintAgent.get();
    return fingerprintResult?.visitorId || 'fingerprint-unavailable';
  } catch (error) {
    return 'fingerprint-unavailable';
  }
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const amount = Number(value);
  if (Number.isNaN(amount)) return '-';

  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatTna = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const rate = Number(value);
  if (Number.isNaN(rate)) return '-';
  return `${tnaDecimalToCalculatorPercent(rate).toFixed(2)}%`;
};

const formatDate = (value) => formatLongDate(value);

const MobileCommerceSignaturePage = () => {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusData, setStatusData] = useState(null);
  const [currentStep, setCurrentStep] = useState(STEP_SUMMARY);
  const [isLoading, setIsLoading] = useState(true);
  const [isSigning, setIsSigning] = useState(false);
  const [isIdentityValidated, setIsIdentityValidated] = useState(() => readIdentityValidated(token));
  const [acceptedContract, setAcceptedContract] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isPreparingIdentity, setIsPreparingIdentity] = useState(false);
  const [identityRequestFailed, setIdentityRequestFailed] = useState(false);
  const [bypassRequestFailed, setBypassRequestFailed] = useState(false);
  const isPreparingIdentityRef = useRef(false);

  usePageTitle('Firma de contrato');

  const scope = useMemo(() => getStoredCommerceSignatureScope(), []);
  const requestUid = statusData?.request_uid || scope?.request_uid || '';
  const canSign = statusData?.status === 'pending' && statusData?.can_sign === true;
  const statusMessage = STATUS_MESSAGES[statusData?.status] || statusData?.message || '';
  const loanSummary = statusData?.loan_summary || {};
  const identityBypassAvailable = statusData?.identity_bypass_available === true;

  const markIdentityValidated = useCallback(() => {
    writeIdentityValidated(token);
    setIsIdentityValidated(true);
    setCurrentStep(STEP_SIGN);
    setErrorMessage('');
  }, [token]);

  const loadStatus = useCallback(async () => {
    if (!token) return;

    try {
      setIsLoading(true);
      setErrorMessage('');
      const data = await fetchCommerceMobileSignatureStatus(token);
      setStatusData(data);
      if (data?.status === 'completed') {
        setCurrentStep(STEP_SUCCESS);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'No se pudo consultar el estado del contrato.');
      setStatusData(null);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      localStorage.setItem(ORIGINAL_TOKEN_KEY, token);
    }
  }, [token]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const fromStep = searchParams.get('from_step');
    if (fromStep !== 'identity_validation') {
      return;
    }

    markIdentityValidated();

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('from_step');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, markIdentityValidated]);

  const resetPreparingIdentity = () => {
    isPreparingIdentityRef.current = false;
    setIsPreparingIdentity(false);
  };

  const startIdentityRequest = () => {
    if (isPreparingIdentityRef.current) {
      return false;
    }

    isPreparingIdentityRef.current = true;
    setIsPreparingIdentity(true);
    setErrorMessage('');
    return true;
  };

  const handleValidateIdentity = async () => {
    if (!startIdentityRequest()) {
      return;
    }

    setIdentityRequestFailed(false);

    try {
      if (!requestUid) {
        throw new Error('No se encontró la solicitud para validar identidad.');
      }

      const result = await fetchIdentityVerification(requestUid);
      const verificationUrl = result.verificationUrl || null;

      if (!verificationUrl) {
        throw new Error('No se pudo obtener el enlace de Valida. Intentá de nuevo.');
      }

      window.location.href = verificationUrl;
    } catch (error) {
      setIdentityRequestFailed(true);
      setErrorMessage(
        error.response?.data?.message
        || error.message
        || 'No se pudo obtener el enlace de validación de identidad.'
      );
      resetPreparingIdentity();
    }
  };

  const handleIdentityBypass = async () => {
    if (!startIdentityRequest()) {
      return;
    }

    setBypassRequestFailed(false);

    try {
      if (!requestUid) {
        throw new Error('No se encontró la solicitud para validar identidad.');
      }

      const result = await fetchIdentityVerification(requestUid, { bypass: true });
      const testingUrl = result.testingUrl || null;

      if (!testingUrl) {
        throw new Error('No se pudo obtener el enlace de prueba. Intentá de nuevo.');
      }

      window.location.href = testingUrl;
    } catch (error) {
      setBypassRequestFailed(true);
      setErrorMessage(
        error.response?.data?.message
        || error.response?.data?.error?.message
        || error.message
        || 'No se pudo continuar sin validación. Intentá de nuevo.'
      );
      resetPreparingIdentity();
    }
  };

  const handleSign = async () => {
    if (!acceptedContract || !canSign || !isIdentityValidated || isSigning) return;

    try {
      setIsSigning(true);
      setErrorMessage('');

      const fingerprint = await getFingerprintVisitorId();

      const response = await signCommerceMobileContract(token, {
        screenResolution: getScreenResolution(),
        fingerprint
      });

      setStatusData((current) => ({
        ...(current || {}),
        ...response,
        status: response.status || 'completed',
        can_sign: false,
        message: response.message || 'Contrato firmado exitosamente'
      }));
      setAcceptedContract(false);
      setCurrentStep(STEP_SUCCESS);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'No se pudo firmar el contrato. Intentá nuevamente.');
    } finally {
      setIsSigning(false);
    }
  };

  const renderLoading = () => (
    <div className="text-center py-5">
      <div className="spinner-border text-primary mb-3" role="status">
        <span className="visually-hidden">Cargando...</span>
      </div>
      <p className="text-muted mb-0">validando link de firma...</p>
    </div>
  );

  const renderLoanSummary = () => {
    const rows = [
      ['Capital solicitado', formatCurrency(loanSummary.amount)],
      ['Cantidad de cuotas', loanSummary.installments ?? loanSummary.term ?? '-'],
      ['Valor de cuota', formatCurrency(loanSummary.monthlyPayment ?? loanSummary.installmentAmount)],
      ['TNA', formatTna(loanSummary.interestRate ?? loanSummary.taxes)],
      ['Primer vencimiento', formatDate(loanSummary.firstDueDate)]
    ];

    return (
      <section className="text-start">
        <h1 className="h4 mb-2">Revisá tu préstamo</h1>
        <p className="text-muted small mb-2">
          Antes de firmar, confirmá que los datos sean correctos.
        </p>
        <p className="text-muted small mb-4">
          Te vamos a redirigir a Valida para confirmar tu identidad.
        </p>

        <div className="list-group mb-4">
          {rows.map(([label, value]) => (
            <div key={label} className="list-group-item d-flex justify-content-between gap-3">
              <span className="text-muted">{label}</span>
              <strong className="text-end">{value || '-'}</strong>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="btn btn-primary w-100"
          onClick={handleValidateIdentity}
          disabled={!canSign || isPreparingIdentity}
        >
          {isPreparingIdentity
            ? 'Preparando validación...'
            : identityRequestFailed
              ? 'Reintentar'
              : 'Validar identidad'}
        </button>

        {identityBypassAvailable ? (
          <div className="mt-3 text-center">
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-muted text-decoration-none"
              onClick={handleIdentityBypass}
              disabled={!canSign || isPreparingIdentity}
            >
              {bypassRequestFailed
                ? 'Reintentar sin validación'
                : 'Continuar sin validación (ambiente de prueba)'}
            </button>
          </div>
        ) : null}

        {statusData?.contract_url && (
          <div className="text-center mt-3">
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              onClick={() => setShowContractModal(true)}
            >
              Ver contrato
            </button>
          </div>
        )}
      </section>
    );
  };

  const renderSignStep = () => (
    <section className="text-start">
      <h1 className="h4 mb-2">Firmá el contrato</h1>
      <p className="text-muted small mb-4">
        Leé el contrato y aceptá para finalizar la firma.
      </p>

      {statusData?.contract_url && (
        <button
          type="button"
          className="btn btn-outline-primary w-100 mb-3"
          onClick={() => setShowContractModal(true)}
        >
          Ver contrato
        </button>
      )}

      <div className="form-check text-start mb-3">
        <input
          id="accept-commerce-contract"
          className="form-check-input"
          type="checkbox"
          checked={acceptedContract}
          onChange={(event) => setAcceptedContract(event.target.checked)}
        />
        <label className="form-check-label" htmlFor="accept-commerce-contract">
          Leí y acepto el contrato
        </label>
      </div>

      <button
        type="button"
        className="btn btn-primary w-100"
        disabled={!acceptedContract || !isIdentityValidated || isSigning}
        onClick={handleSign}
      >
        {isSigning ? 'Firmando contrato...' : 'Firmar contrato'}
      </button>
    </section>
  );

  const renderSuccess = () => (
    <section className="text-center py-4">
      <i className="bi bi-check-circle text-success mb-3" style={{ fontSize: '3rem' }}></i>
      <h1 className="h4 mb-3">Firma completada</h1>
      <p className="text-muted mb-0">
        Ya finalizaste la firma del préstamo. Avisale al comercial para continuar con la operación.
      </p>
    </section>
  );

  const renderTerminalStatus = () => (
    <div className={`alert ${statusData?.status === 'completed' ? 'alert-success' : 'alert-warning'}`}>
      {statusMessage || 'No se puede firmar el contrato con este link.'}
    </div>
  );

  const renderCurrentStep = () => {
    if (!canSign) {
      return statusData?.status === 'completed' ? renderSuccess() : renderTerminalStatus();
    }

    if (currentStep === STEP_SIGN && isIdentityValidated) {
      return renderSignStep();
    }

    if (currentStep === STEP_SUCCESS) {
      return renderSuccess();
    }

    if (isIdentityValidated) {
      return renderSignStep();
    }

    return renderLoanSummary();
  };

  return (
    <div className="container py-4">
      <ContractPdfViewerModal
        show={showContractModal}
        onHide={() => setShowContractModal(false)}
        title="Contrato de préstamo"
        url={statusData?.contract_url}
      />

      <div className="card shadow-sm">
        <div className="card-body text-center">
          {isLoading ? (
            renderLoading()
          ) : (
            <div className="mt-4">
              {errorMessage && (
                <div className="alert alert-danger text-start">{errorMessage}</div>
              )}

              {statusData ? renderCurrentStep() : null}

              {(!statusData || (!canSign && statusData?.status !== 'completed')) && (
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 mt-3"
                  onClick={loadStatus}
                >
                  Actualizar estado
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileCommerceSignaturePage;
